import {
  EmojiPicker as Picker,
  type EmojiPickerListCategoryHeaderProps,
  type EmojiPickerListEmojiProps,
  type EmojiPickerListRowProps,
  useSkinTone,
} from "frimousse"
import type { CustomEmojiListing } from "@beacon/core/custom-emoji"
import { SmilePlusIcon } from "lucide-react"
import { useRef, useState } from "react"
import { useTriggerTooltip, useViewport } from "@/design-system/index.ts"
import { Button } from "@/primitives/button.tsx"
import { Popover, PopoverContent, PopoverTrigger } from "@/primitives/popover.tsx"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/primitives/tabs.tsx"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/primitives/tooltip.tsx"
import { cn } from "@/primitives/utils.ts"
import { AddCustomEmoji } from "./add-custom-emoji.tsx"
import type { FoundEmoji } from "./catalog.ts"
import { CustomEmojiPicker } from "./custom-emoji-picker.tsx"
import { emojibaseUrl } from "./hosting.ts"
import { useEmojiCatalog } from "./load.ts"

function CategoryHeader({ category, className, ...props }: EmojiPickerListCategoryHeaderProps) {
  return (
    <div
      {...props}
      className="bg-popover px-3 pt-2.5 pb-1 text-xs font-medium text-muted-foreground"
    >
      {category.label}
    </div>
  )
}

function Row({ children, className, ...props }: EmojiPickerListRowProps) {
  return (
    <div {...props} className="flex scroll-my-1.5 px-1.5 compact:justify-between">
      {children}
    </div>
  )
}

function Emoji({ emoji, className, ...props }: EmojiPickerListEmojiProps) {
  return (
    <button
      {...props}
      className={cn(
        "flex size-8 items-center justify-center rounded-md text-title compact:size-11",
        emoji.isActive && "bg-popover-accent",
      )}
    >
      {emoji.emoji}
    </button>
  )
}

/** The key that moves the focus through a grid of emoji, by how many, for one `columns` wide. */
const stepOf = (key: string, columns: number) =>
  ({ ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns })[key]

/** How many emoji a search offers at most. */
const foundAtMost = 64

/*
 * What a search finds, likeliest first in one grid (the catalog's ranking, so `party` offers Party
 * popper first, as the `:` menu does), in the skin tone the picker is set to.
 */
function Found({
  found,
  onPick,
  onActive,
}: {
  found: readonly FoundEmoji[]
  onPick: (emoji: string) => void
  onActive: (emoji: { emoji: string; label: string }) => void
}) {
  const [tone] = useSkinTone()
  const shown = (emoji: FoundEmoji) => (tone !== "none" && emoji.skins?.[tone]) || emoji.emoji
  return (
    <ul
      aria-label="Emoji found"
      onKeyDown={(event) => {
        const grid = event.currentTarget
        const columns = getComputedStyle(grid).gridTemplateColumns.split(" ").length || 1
        const step = stepOf(event.key, columns)
        if (step === undefined) return
        const buttons = [...grid.querySelectorAll<HTMLButtonElement>("button")]
        const next = buttons[buttons.indexOf(document.activeElement as HTMLButtonElement) + step]
        if (!next) return
        event.preventDefault()
        next.focus()
      }}
      className="grid grid-cols-8 px-1.5 pt-1.5 pb-1.5 compact:justify-items-center"
    >
      {found.map((emoji) => (
        <li key={emoji.emoji} className="flex">
          <button
            type="button"
            aria-label={emoji.label}
            onClick={() => onPick(shown(emoji))}
            onMouseEnter={() => onActive({ emoji: shown(emoji), label: emoji.label })}
            onFocus={() => onActive({ emoji: shown(emoji), label: emoji.label })}
            className="flex size-8 items-center justify-center rounded-md font-emoji text-title outline-none hover:bg-popover-accent focus-visible:bg-popover-accent focus-visible:ring-2 focus-visible:ring-ring compact:size-11"
          >
            {shown(emoji)}
          </button>
        </li>
      ))}
    </ul>
  )
}

/*
 * Every standard emoji, by category (frimousse, on Emojibase's data from the web app's own origin,
 * `hosting.ts`), and a search that finds one by its shortcode, its name or a word for it, likeliest
 * first (`Found`). It offers only the emoji this device can show, so none is picked that would
 * arrive as a box. Enter in the search picks the first found and the arrow keys go on into them;
 * the emoji under the pointer or the keys is named at its foot, beside the skin tone the hands and
 * people come in.
 */
export function EmojiPicker({ onPick }: { onPick: (emoji: string) => void }) {
  const { touch } = useViewport()
  const catalog = useEmojiCatalog()
  const [query, setQuery] = useState("")
  const [active, setActive] = useState<{ emoji: string; label: string }>()
  const sought = query.trim()
  const found = sought && catalog ? catalog.search(sought, foundAtMost) : undefined
  const results = useRef<HTMLDivElement>(null)
  return (
    <Picker.Root
      emojibaseUrl={emojibaseUrl}
      columns={8}
      onEmojiSelect={({ emoji }) => onPick(emoji)}
      className="isolate flex h-80 w-full flex-col"
    >
      <input
        type="search"
        aria-label="Find an emoji"
        placeholder="Find an emoji"
        value={query}
        autoComplete="off"
        spellCheck={false}
        // On touch the keyboard waits for a tap, so it doesn't rise over the emoji as they open.
        autoFocus={!touch}
        onChange={(event) => {
          setQuery(event.target.value)
          setActive(undefined)
        }}
        onKeyDown={(event) => {
          if (!found?.[0]) return
          if (event.key === "Enter") {
            event.preventDefault()
            results.current?.querySelector("button")?.click()
          } else if (event.key === "ArrowDown") {
            event.preventDefault()
            results.current?.querySelector("button")?.focus()
          }
        }}
        className="mx-2 mt-2 h-8 rounded-md bg-surface-sunken px-2.5 text-ui ring-1 ring-border outline-none ring-inset placeholder:text-subtle-foreground focus-visible:ring-ring/40 touch:h-10 touch:text-field"
      />
      {sought ? (
        <div ref={results} className="relative mt-1 min-h-0 flex-1 overflow-y-auto">
          {!found ? (
            <p className="absolute inset-0 flex items-center justify-center text-ui text-subtle-foreground">
              Loading emoji…
            </p>
          ) : found.length === 0 ? (
            <p className="absolute inset-0 flex items-center justify-center text-ui text-subtle-foreground">
              No emoji match
            </p>
          ) : (
            <Found found={found} onPick={onPick} onActive={setActive} />
          )}
        </div>
      ) : (
        <Picker.Viewport className="relative mt-1 flex-1 outline-none">
          <Picker.Loading className="absolute inset-0 flex items-center justify-center text-ui text-subtle-foreground">
            Loading emoji…
          </Picker.Loading>
          <Picker.List className="pb-1.5 select-none" components={{ CategoryHeader, Row, Emoji }} />
        </Picker.Viewport>
      )}
      <div className="flex h-10 items-center gap-2 border-t border-border-subtle px-3">
        <Picker.ActiveEmoji>
          {({ emoji: pointed }) => {
            const emoji = sought ? active : pointed
            return emoji ? (
              <>
                <span aria-hidden className="text-title">
                  {emoji.emoji}
                </span>
                <span className="min-w-0 flex-1 truncate text-ui text-muted-foreground">
                  {emoji.label}
                </span>
              </>
            ) : (
              <span className="min-w-0 flex-1 truncate text-ui text-subtle-foreground">
                Pick an emoji…
              </span>
            )
          }}
        </Picker.ActiveEmoji>
        <Picker.SkinToneSelector
          aria-label="Skin tone"
          className="flex size-7 shrink-0 items-center justify-center rounded-md text-title hover:bg-popover-accent focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
        />
      </div>
    </Picker.Root>
  )
}

/*
 * A button that opens the emoji picker (`EmojiPicker`): anchored above it on the desk, and a sheet on
 * `compact`, as every popover is. A pick goes to `onPick` and closes it; however it closes,
 * `focusAfter` says where the focus goes, as a composer puts it back in its text. A press on the
 * desk keeps the focus where it was, so a text's caret stays put for the emoji.
 *
 * With `onPickCustom`, the picker has two tabs: Standard, and Custom, the company's own emoji
 * (`CustomEmojiPicker`), whose pick goes there instead, and whose Add emoji closes the picker and
 * opens `AddCustomEmoji`.
 */
export function EmojiButton({
  onPick,
  onPickCustom,
  focusAfter,
  label = "Emoji",
  open: given,
  onOpenChange,
}: {
  onPick: (emoji: string) => void
  onPickCustom?: (emoji: CustomEmojiListing) => void
  focusAfter?: () => void
  label?: string
  /** Whether the picker is open, where something else opens it too (a message's action sheet). */
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const [own, setOwn] = useState(false)
  const open = given ?? own
  const setOpen = (next: boolean) => {
    setOwn(next)
    onOpenChange?.(next)
  }
  const [adding, setAdding] = useState(false)
  const tip = useTriggerTooltip(open)
  const standard = (
    <EmojiPicker
      onPick={(emoji) => {
        onPick(emoji)
        setOpen(false)
      }}
    />
  )
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <Tooltip {...tip.tooltip}>
          <TooltipTrigger
            render={
              <PopoverTrigger
                render={
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    aria-label={label}
                    onMouseDown={(event) => event.preventDefault()}
                  />
                }
              />
            }
            {...tip.trigger}
          >
            <SmilePlusIcon />
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
        <PopoverContent
          title="Emoji"
          side="top"
          align="end"
          className="w-72 gap-0 overflow-hidden p-0"
          {...(focusAfter && {
            finalFocus: () => {
              focusAfter()
              return false
            },
          })}
        >
          {onPickCustom ? (
            <Tabs defaultValue="standard" className="gap-0">
              <TabsList aria-label="Emoji sets" className="mx-2 mt-2 w-auto self-stretch">
                <TabsTrigger value="standard">Standard</TabsTrigger>
                <TabsTrigger value="custom">Custom</TabsTrigger>
              </TabsList>
              <TabsContent value="standard">{standard}</TabsContent>
              <TabsContent value="custom">
                <CustomEmojiPicker
                  onPick={(emoji) => {
                    onPickCustom(emoji)
                    setOpen(false)
                  }}
                  onAdd={() => {
                    setOpen(false)
                    setAdding(true)
                  }}
                />
              </TabsContent>
            </Tabs>
          ) : (
            standard
          )}
        </PopoverContent>
      </Popover>
      {onPickCustom && <AddCustomEmoji open={adding} onOpenChange={setAdding} />}
    </>
  )
}
